import { useMemo } from "react";

import QUERY_TAGS from "../search/queryTags";
import useQuery from "../search/useQuery";
import { quantiseCoordinates, radiusToMeters } from "../search/supplierFilters";

import {
  fetchNearbyOffers,
  fetchProduct,
  fetchProductSuppliers,
} from "./detailService";
import {
  mapNearbyOffer,
  mapOriginOffer,
  mapProduct,
  mapSupplierOffer,
} from "./detailMappers";
import {
  buildFeed,
  distanceBoundKm,
  distanceCap,
  mergeOffers,
  resolveProductId,
} from "./productSuppliers";

const useProductSuppliers = ({
  entryProductId = null,
  volume = null,
  containerOptionId = null,
  coordinates = null,
  radiusKm = null,
  filters,
}) => {
  const cell = useMemo(() => quantiseCoordinates(coordinates), [coordinates]);

  const nearbyEnabled =
    Boolean(cell) && !entryProductId && Number.isFinite(volume);

  const nearbyKey = nearbyEnabled
    ? `product-nearby:${cell.latitude},${cell.longitude},${radiusToMeters(
        radiusKm
      )},${volume},${containerOptionId || ""}`
    : null;

  const nearby = useQuery({
    key: nearbyKey,
    tag: QUERY_TAGS.PRODUCT_OFFERS,
    enabled: nearbyEnabled,
    fetcher: () =>
      fetchNearbyOffers({
        coordinates: cell,
        radiusKm,
        volume,
        containerOptionId,
      }),
  });

  const nearbyCards = useMemo(
    () =>
      (nearby.currentData || [])
        .map((row) => mapNearbyOffer(row, cell))
        .filter(Boolean),
    [nearby.currentData, cell]
  );

  const productId = entryProductId ?? resolveProductId(nearbyCards);

  const productKey = productId ? `product:${productId}` : null;

  const product = useQuery({
    key: productKey,
    tag: QUERY_TAGS.PRODUCT_DETAIL,
    enabled: Boolean(entryProductId) && Boolean(productId),
    fetcher: () => fetchProduct(productId),
  });

  const mappedProduct = useMemo(
    () => mapProduct(product.currentData),
    [product.currentData]
  );

  const capKm = distanceCap(filters.maxDistanceKm, radiusKm);

  const offersKey = productId
    ? `product-offers:${productId},${cell ? `${cell.latitude},${cell.longitude}` : "-"},${
        filters.sort
      },${capKm ?? "-"},${filters.includeOutOfStock ? 1 : 0},${
        filters.discountedOnly ? 1 : 0
      }`
    : null;

  const offers = useQuery({
    key: offersKey,
    tag: QUERY_TAGS.PRODUCT_OFFERS,
    enabled: Boolean(productId),
    fetcher: () =>
      fetchProductSuppliers({
        productId,
        coordinates: cell,
        sort: filters.sort,
        maxDistanceKm: capKm,
        includeOutOfStock: filters.includeOutOfStock,
        discountedOnly: filters.discountedOnly,
      }),
  });

  const offerCards = useMemo(
    () =>
      (offers.currentData || [])
        .map((row) => mapSupplierOffer(row, cell))
        .filter(Boolean),
    [offers.currentData, cell]
  );

  const originCard = useMemo(
    () => (mappedProduct ? mapOriginOffer(mappedProduct, cell) : null),
    [mappedProduct, cell]
  );

  const suppliers = useMemo(
    () =>
      buildFeed(
        mergeOffers({
          nearby: nearbyCards,
          origin: originCard,
          offers: offerCards,
        }),
        {
          distanceCapKm: cell ? capKm : null,
          includeOutOfStock: filters.includeOutOfStock,
          discountedOnly: filters.discountedOnly,
          minRating: filters.minRating,
          sort: filters.sort,
        }
      ),
    [nearbyCards, originCard, offerCards, capKm, cell, filters]
  );

  const productTitle = useMemo(() => {
    if (Number.isFinite(volume)) return `${volume}Kg Cylinder`;
    if (mappedProduct?.volume) return `${mappedProduct.volume}Kg Cylinder`;
    if (mappedProduct?.name) return mappedProduct.name;

    return "Cylinder";
  }, [volume, mappedProduct]);

  const productImages = useMemo(() => {
    if (mappedProduct?.imageUrls?.length) return mappedProduct.imageUrls;

    const withImages = nearbyCards.find((card) => card.imageUrls?.length);

    return withImages?.imageUrls || [];
  }, [mappedProduct, nearbyCards]);

  const unavailableCount = suppliers.filter((card) => !card.deliverable).length;

  const isFetching = nearby.isFetching || product.isFetching || offers.isFetching;

  const isResolving =
    (nearbyEnabled && !nearby.isSuccess && !nearby.isError) ||
    (Boolean(productId) && !offers.isSuccess && !offers.isError);

  return {
    productId,
    product: mappedProduct,
    productTitle,
    productImages,
    suppliers,
    total: suppliers.length,
    unavailableCount,
    bounds: { maxDistanceKm: distanceBoundKm(radiusKm) },
    isResolving,
    isFetching,
    error: offers.error || product.error || nearby.error,
    refetch: offers.refetch,
  };
};

export default useProductSuppliers;
